<script>
import Layout from "../../layouts/main";
import PageHeader from "../../components/page-header";

export default {
    components: { Layout, PageHeader },
    data() {
        return {
            title: "Estadísticas de Digitalización",
            items: [
                { text: "Digitalizar", href: "/" },
                { text: "Estadísticas", active: true }
            ],
            asociaciones: [],
            loading: false,
            anioSeleccionado: new Date().getFullYear(),
            costoPorPagina: 15,
            costoMantencion: 35000,
            tooltip: { visible: false, text: '', x: 0, y: 0 },
        };
    },
    computed: {
        anios() {
            const y = new Date().getFullYear();
            return [y - 2, y - 1, y];
        },
        // Agrupa asociaciones por mes del año seleccionado
        datosPorMes() {
            const meses = Array(12).fill(0);
            this.asociaciones.forEach(item => {
                const fecha = item.created_at || item.updated_at;
                if (!fecha) return;
                const d = new Date(fecha);
                if (d.getFullYear() === this.anioSeleccionado) {
                    meses[d.getMonth()] += Number(item.total_paginas_contables || item.total_imagenes || 0);
                }
            });
            return meses;
        },
        meses() {
            return ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
        },
        maxValor() {
            return Math.max(...this.datosPorMes, 1);
        },
        totalPaginasAnio() {
            return this.datosPorMes.reduce((a, b) => a + b, 0);
        },
        totalAsociaciones() {
            return this.asociaciones.filter(item => {
                const fecha = item.created_at || item.updated_at;
                if (!fecha) return false;
                return new Date(fecha).getFullYear() === this.anioSeleccionado;
            }).length;
        },
        completadas() {
            return this.asociaciones.filter(item => {
                const fecha = item.created_at || item.updated_at;
                if (!fecha) return false;
                if (new Date(fecha).getFullYear() !== this.anioSeleccionado) return false;
                return item.total_imagenes > 0 && item.imagenes_digitalizadas >= item.total_imagenes;
            }).length;
        },
        promedioMensual() {
            const conDatos = this.datosPorMes.filter(v => v > 0);
            return conDatos.length ? Math.round(conDatos.reduce((a, b) => a + b, 0) / conDatos.length) : 0;
        },
        mesMaximo() {
            const max = Math.max(...this.datosPorMes);
            const idx = this.datosPorMes.indexOf(max);
            return { paginas: max, nombre: this.meses[idx] };
        },
        tablaMeses() {
            return this.meses.map((nombre, i) => ({
                nombre,
                paginas: this.datosPorMes[i],
            })).filter(m => m.paginas > 0);
        },
        tablaMesesCosto() {
            return this.meses.map((nombre, i) => ({
                nombre,
                paginas: this.datosPorMes[i],
                costo: this.datosPorMes[i] * this.costoPorPagina,
                mantencion: this.costoMantencion,
            })).filter(m => m.paginas > 0);
        },
        costoTotalAnio() { return this.totalPaginasAnio * this.costoPorPagina; },
        costoMantencionAnio() { return this.tablaMesesCosto.length * this.costoMantencion; },
        subTotalNeto() { return this.costoTotalAnio + this.costoMantencionAnio; },
        ivaAnio() { return Math.round(this.subTotalNeto * 0.19); },
        totalConIvaAnio() { return this.subTotalNeto + this.ivaAnio; },
    },
    methods: {
        fetchData() {
            this.loading = true;
            axios.get('/api/get_asociados', { params: { year: this.anioSeleccionado, include_deleted_usage: 1 } }).then(r => {
                this.asociaciones = r.data.asociados || [];
            }).catch(e => {
                console.error(e);
                this.asociaciones = [];
            })
            .finally(() => { this.loading = false; });
        },
        barHeight(paginas) {
            return paginas > 0 ? `${(paginas / this.maxValor) * 100}%` : '3px';
        },
        barColor(paginas) {
            const ratio = paginas / this.maxValor;
            if (ratio >= 0.8) return '#252C5F';
            if (ratio >= 0.5) return '#3a5ca8';
            return '#61B7E4';
        },
        costoBarColor(paginas) {
            const ratio = paginas / this.maxValor;
            if (ratio >= 0.8) return '#1a6fa8';
            if (ratio >= 0.5) return '#4a9dc9';
            return '#61B7E4';
        },
        formatPesos(valor) {
            return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(valor);
        },
        showTooltip(event, text) {
            const rect = event.target.getBoundingClientRect();
            this.tooltip = { visible: true, text, x: rect.left + rect.width / 2, y: rect.top - 10 };
        },
        hideTooltip() {
            this.tooltip.visible = false;
        },
    },
    watch: {
        anioSeleccionado() {
            this.fetchData();
        },
    },
    mounted() {
        this.fetchData();
    }
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <!-- Selector de año -->
        <div class="row mb-4 align-items-center">
            <div class="col-md-3">
                <b-form-select v-model="anioSeleccionado" :options="anios.map(a => ({ value: a, text: a }))" />
            </div>
            <div class="col-md-9 d-flex justify-content-end">
                <b-button variant="outline-primary" @click="fetchData" :disabled="loading">
                    <b-spinner small v-if="loading" class="mr-1"></b-spinner>
                    <i class="bx bx-refresh mr-1" v-else></i> Actualizar
                </b-button>
            </div>
        </div>

        <!-- Tarjetas resumen -->
        <div class="row mb-4">
            <div class="col-sm-6 col-xl-3">
                <div class="card text-center">
                    <div class="card-body">
                        <i class="bx bx-images font-size-36 mb-2" style="color:#252C5F;"></i>
                        <h4 class="font-weight-bold mb-1" style="color:#252C5F;">{{ totalPaginasAnio.toLocaleString('es-CL') }}</h4>
                        <p class="text-muted mb-0 small">Total lecturas de paginas {{ anioSeleccionado }}</p>
                    </div>
                </div>
            </div>
            <div class="col-sm-6 col-xl-3">
                <div class="card text-center">
                    <div class="card-body">
                        <i class="bx bx-link-alt font-size-36 mb-2" style="color:#61B7E4;"></i>
                        <h4 class="font-weight-bold mb-1" style="color:#252C5F;">{{ totalAsociaciones.toLocaleString('es-CL') }}</h4>
                        <p class="text-muted mb-0 small">Asociaciones {{ anioSeleccionado }}</p>
                    </div>
                </div>
            </div>
            <div class="col-sm-6 col-xl-3">
                <div class="card text-center">
                    <div class="card-body">
                        <i class="bx bx-trending-up font-size-36 mb-2" style="color:#CB9700;"></i>
                        <h4 class="font-weight-bold mb-1" style="color:#252C5F;">{{ promedioMensual.toLocaleString('es-CL') }}</h4>
                        <p class="text-muted mb-0 small">Promedio mensual (fichas)</p>
                    </div>
                </div>
            </div>
            <div class="col-sm-6 col-xl-3">
                <div class="card text-center">
                    <div class="card-body">
                        <i class="bx bx-trophy font-size-36 mb-2" style="color:#28a745;"></i>
                        <h4 class="font-weight-bold mb-1" style="color:#252C5F;">{{ completadas }}</h4>
                        <p class="text-muted mb-0 small">Asociaciones completadas</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Gráfico de barras -->
        <div class="row">
            <div class="col-12">
                <div class="card mb-4">
                    <div class="card-header py-3 d-flex align-items-center"
                        style="background: linear-gradient(135deg, #252C5F 0%, #61B7E4 100%); border-radius: 4px 4px 0 0;">
                        <i class="bx bx-bar-chart-alt-2 font-size-22 text-white mr-2"></i>
                        <h5 class="text-white mb-0 font-weight-bold">Lecturas de paginas por mes — {{ anioSeleccionado }}</h5>
                    </div>
                    <div class="card-body">
                        <div v-if="loading" class="text-center py-5">
                            <b-spinner variant="primary"></b-spinner>
                        </div>
                        <div v-else style="display:flex; height:260px;">
                            <!-- Eje Y -->
                            <div style="width:56px;flex-shrink:0;display:flex;flex-direction:column;justify-content:space-between;padding-bottom:36px;align-items:flex-end;padding-right:8px;">
                                <span v-for="n in 5" :key="n" style="font-size:11px;color:#666;">
                                    {{ Math.round((maxValor * (5 - n)) / 4).toLocaleString('es-CL') }}
                                </span>
                            </div>
                            <!-- Área barras -->
                            <div style="flex:1;display:flex;flex-direction:column;">
                                <div style="flex:1;position:relative;border-left:2px solid #e0e0e0;border-bottom:2px solid #e0e0e0;">
                                    <!-- Líneas guía -->
                                    <div v-for="n in 4" :key="n"
                                        style="position:absolute;left:0;right:0;border-top:1px dashed #e0e0e0;"
                                        :style="{ top: `${((n-1)/4)*100}%` }" />
                                    <!-- Barras -->
                                    <div style="position:absolute;inset:0;display:flex;align-items:flex-end;gap:4px;padding:0 4px;">
                                        <div v-for="(paginas, i) in datosPorMes" :key="i"
                                            style="flex:1;display:flex;flex-direction:column;align-items:center;height:100%;">
                                            <div style="flex:1;width:100%;display:flex;align-items:flex-end;">
                                                <div
                                                    :style="{
                                                        width: '100%',
                                                        height: barHeight(paginas),
                                                        backgroundColor: paginas > 0 ? barColor(paginas) : '#e8e8e8',
                                                        borderRadius: '4px 4px 0 0',
                                                        transition: 'all 0.3s ease',
                                                        cursor: paginas > 0 ? 'pointer' : 'default',
                                                    }"
                                                    @mouseenter="paginas > 0 && showTooltip($event, `${meses[i]}: ${paginas.toLocaleString('es-CL')} lecturas`)"
                                                    @mouseleave="hideTooltip"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- Etiquetas -->
                                <div style="height:36px;display:flex;gap:4px;padding:0 4px 0 6px;">
                                    <div v-for="(_, i) in datosPorMes" :key="i"
                                        style="flex:1;text-align:center;padding-top:4px;">
                                        <span style="font-size:10px;color:#666;">{{ meses[i].substring(0,3) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tabla detalle por mes -->
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header py-3 d-flex align-items-center"
                        style="background: linear-gradient(135deg, #252C5F 0%, #61B7E4 100%); border-radius: 4px 4px 0 0;">
                        <i class="bx bx-table font-size-22 text-white mr-2"></i>
                        <h5 class="text-white mb-0 font-weight-bold">Detalle mensual — {{ anioSeleccionado }}</h5>
                    </div>
                    <div class="card-body p-0">
                        <div v-if="tablaMeses.length === 0" class="text-center py-5">
                            <i class="bx bx-inbox font-size-48 text-muted d-block mb-2"></i>
                            <p class="text-muted">Sin datos para el año {{ anioSeleccionado }}</p>
                        </div>
                        <div v-else class="table-responsive">
                            <table class="table table-hover mb-0">
                                <thead class="thead-light">
                                    <tr>
                                        <th>Mes</th>
                                        <th class="text-right">Lecturas de paginas</th>
                                        <th style="min-width:160px;">Distribución</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(fila, i) in tablaMeses" :key="i">
                                        <td class="font-weight-medium">{{ fila.nombre }}</td>
                                        <td class="text-right">
                                            <b-badge variant="primary" pill>{{ fila.paginas.toLocaleString('es-CL') }}</b-badge>
                                        </td>
                                        <td>
                                            <b-progress :value="fila.paginas" :max="maxValor" height="10px">
                                                <b-progress-bar :value="fila.paginas" :max="maxValor" variant="primary"></b-progress-bar>
                                            </b-progress>
                                        </td>
                                    </tr>
                                </tbody>
                                <tfoot style="border-top: 2px solid #252C5F;">
                                    <tr>
                                        <td class="font-weight-bold" style="color:#252C5F;">Total</td>
                                        <td class="text-right">
                                            <b-badge variant="primary" pill class="font-size-13">{{ totalPaginasAnio.toLocaleString('es-CL') }}</b-badge>
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Gráfico de costos -->
        <div class="row">
            <div class="col-12">
                <div class="card mb-4">
                    <div class="card-header py-3 d-flex align-items-center"
                        style="background: linear-gradient(135deg, #252C5F 0%, #61B7E4 100%); border-radius: 4px 4px 0 0;">
                        <i class="bx bx-line-chart font-size-22 text-white mr-2"></i>
                        <h5 class="text-white mb-0 font-weight-bold">Costo mensual (CLP) — {{ anioSeleccionado }}</h5>
                    </div>
                    <div class="card-body">
                        <div style="display:flex;height:260px;">
                            <div style="width:80px;flex-shrink:0;display:flex;flex-direction:column;justify-content:space-between;padding-bottom:36px;align-items:flex-end;padding-right:8px;">
                                <span v-for="n in 5" :key="n" style="font-size:9px;color:#666;line-height:1;text-align:right;">
                                    {{ formatPesos(Math.round((maxValor * costoPorPagina * (5-n)) / 4)) }}
                                </span>
                            </div>
                            <div style="flex:1;display:flex;flex-direction:column;">
                                <div style="flex:1;position:relative;border-left:2px solid #e0e0e0;border-bottom:2px solid #e0e0e0;">
                                    <div v-for="n in 4" :key="n" style="position:absolute;left:0;right:0;border-top:1px dashed #e0e0e0;" :style="{ top: `${((n-1)/4)*100}%` }" />
                                    <div style="position:absolute;inset:0;display:flex;align-items:flex-end;gap:4px;padding:0 4px;">
                                        <div v-for="(paginas, i) in datosPorMes" :key="i" style="flex:1;display:flex;flex-direction:column;align-items:center;height:100%;">
                                            <div style="flex:1;width:100%;display:flex;align-items:flex-end;">
                                                <div
                                                    :style="{ width:'100%', height: barHeight(paginas), backgroundColor: paginas > 0 ? costoBarColor(paginas) : '#e8e8e8', borderRadius:'4px 4px 0 0', transition:'all 0.3s ease', cursor: paginas > 0 ? 'pointer' : 'default' }"
                                                    @mouseenter="paginas > 0 && showTooltip($event, `${meses[i]}: ${formatPesos(paginas * costoPorPagina)}`)"
                                                    @mouseleave="hideTooltip"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div style="height:36px;display:flex;gap:4px;padding:0 4px 0 6px;">
                                    <div v-for="(_, i) in datosPorMes" :key="i" style="flex:1;text-align:center;padding-top:4px;">
                                        <span style="font-size:10px;color:#666;">{{ meses[i].substring(0,3) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tabla resumen de costos -->
        <div class="row">
            <div class="col-12">
                <div class="card mb-4">
                    <div class="card-header py-3 d-flex align-items-center"
                        style="background: linear-gradient(135deg, #252C5F 0%, #61B7E4 100%); border-radius: 4px 4px 0 0;">
                        <i class="bx bx-receipt font-size-22 text-white mr-2"></i>
                        <h5 class="text-white mb-0 font-weight-bold">Resumen de costos — {{ anioSeleccionado }}</h5>
                    </div>
                    <div class="card-body p-0">
                        <div class="table-responsive">
                            <table class="table table-sm mb-0">
                                <thead class="thead-light">
                                    <tr>
                                        <th>Mes</th>
                                        <th class="text-right">Fichas procesadas</th>
                                        <th class="text-right">Costo fichas (CLP)</th>
                                        <th class="text-right">Mantención (CLP)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="tablaMesesCosto.length === 0">
                                        <td colspan="4" class="text-center text-muted py-4">Sin datos para el año {{ anioSeleccionado }}</td>
                                    </tr>
                                    <tr v-for="fila in tablaMesesCosto" :key="fila.nombre">
                                        <td class="font-weight-medium">{{ fila.nombre }}</td>
                                        <td class="text-right">{{ fila.paginas.toLocaleString('es-CL') }}</td>
                                        <td class="text-right"><b-badge variant="primary" pill>{{ formatPesos(fila.costo) }}</b-badge></td>
                                        <td class="text-right"><b-badge variant="secondary" pill>{{ formatPesos(fila.mantencion) }}</b-badge></td>
                                    </tr>
                                </tbody>
                                <tfoot style="border-top:2px solid #252C5F;">
                                    <tr>
                                        <td colspan="2" class="text-right font-weight-medium" style="color:#252C5F;">Fichas: {{ totalPaginasAnio.toLocaleString('es-CL') }}</td>
                                        <td class="text-right"><b-badge variant="primary">{{ formatPesos(costoTotalAnio) }}</b-badge></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td colspan="2" class="text-right font-weight-medium" style="color:#252C5F;">Mantención ({{ tablaMesesCosto.length }} mes{{ tablaMesesCosto.length !== 1 ? 'es' : '' }}):</td>
                                        <td></td>
                                        <td class="text-right"><b-badge variant="primary">{{ formatPesos(costoMantencionAnio) }}</b-badge></td>
                                    </tr>
                                    <tr style="border-top:1px solid #e0e0e0;">
                                        <td colspan="2" class="text-right font-weight-medium" style="color:#252C5F;">Total Neto:</td>
                                        <td colspan="2" class="text-right"><b-badge variant="primary">{{ formatPesos(subTotalNeto) }}</b-badge></td>
                                    </tr>
                                    <tr>
                                        <td colspan="2" class="text-right font-weight-medium" style="color:#252C5F;">IVA (19%):</td>
                                        <td colspan="2" class="text-right"><b-badge variant="primary">{{ formatPesos(ivaAnio) }}</b-badge></td>
                                    </tr>
                                    <tr>
                                        <td colspan="2" class="text-right font-weight-bold" style="color:#252C5F;font-size:1rem;">Total con IVA:</td>
                                        <td colspan="2" class="text-right"><b-badge variant="primary" style="font-size:1rem;padding:.5rem .8rem;">{{ formatPesos(totalConIvaAnio) }}</b-badge></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tooltip global -->
        <div v-if="tooltip.visible" :style="{
                position: 'fixed',
                left: tooltip.x + 'px',
                top: tooltip.y + 'px',
                transform: 'translate(-50%, -100%)',
                backgroundColor: '#252C5F',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                pointerEvents: 'none',
                zIndex: 9999,
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }">
                {{ tooltip.text }}
            </div>
    </Layout>
</template>

<style scoped>
.table td, .table th { vertical-align: middle; }
</style>
